import React, {
  useCallback,
  useContext,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';
import styled from 'styled-components';
import { FiArrowLeft } from 'react-icons/fi';
import { HiOutlineMicrophone } from 'react-icons/hi';
import { FaArrowRight } from 'react-icons/fa';
import { MdStop } from 'react-icons/md';
import { BiClipboard } from 'react-icons/bi';
import { GrAttachment } from 'react-icons/gr';

import TextareaAutosize from 'react-textarea-autosize';
import { useSignals } from '@preact/signals-react/runtime';
import { Signal, useComputed } from '@preact/signals-react';
import { motion } from 'motion/react';
import { FileDropzone } from '@/features/ai-agents/shared/file-dropzone';
import { ChatImageTile } from '@/features/ai-agents/components/chat-image-tile';
import {
  AddContextButton,
  ChatContextualTile,
  AddContextWithSelectorButton,
} from '@/features/ai-agents/components/chat-contextual-tile';
import { useChatFilesUpload } from '@/features/ai-agents/shared/use-chat-files-upload';
import {
  AiChatReference,
  AiChatMessage,
  chatNow,
  ContextualAttachment,
  ImageData,
  SelectedAgent,
} from '@/features/ai-agents/shared/models';
import { ToolWhiteboard } from '@/features/ai-agents/components/tool-whiteboard';
import {
  ChatVideoUpload,
  ChatVideoUploadHandle,
} from '@/features/ai-agents/components/chat-video-upload';
import { AgentSelectionDropdown } from '@/features/ai-agents/components/agent-selection-dropdown';
import { EditorContext } from '@/features/editor/context/editor-context';
import { ChatReferenceTile } from '@/features/ai-agents/components/chat-reference-tile';

// ... (other code)

export interface ChatInputProps {
  loading?: boolean;
  disabled?: boolean;
  placeholder?: string;
  maxRows?: number;
  onSubmit?: (msg: AiChatMessage) => void;
  onAbort?: () => void;
  showDropZone?: boolean;

  enableFilesUpload?: boolean;
  enableWhiteboard?: boolean;
  enableExamples?: boolean;
  enableVoiceInput?: boolean;
  enableAgentSelection?: boolean;

  enableCustomToolButton?: boolean;
  customToolButtonText?: string;
  onCustomToolButtonClicked?: (ev: any) => void;

  cmdEnterToSend?: boolean;

  contextualAttachments?: Signal<ContextualAttachment[]>;
  references?: Signal<AiChatReference[]>;
  onRemoveReference?: (ref: AiChatReference) => void;
  onFocus?: () => void;
  onBlur?: () => void;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  onEditAttachment?: (
    oldAttachment: ContextualAttachment,
    newSelector: string,
  ) => void;
  onAgentSelect?: (agent: SelectedAgent) => void;
  selectedAgent?: SelectedAgent;
  sessionId?: string;
  onLeaveAnchor?: () => void;

  minLines?: number;

  showSendText?: boolean;
}

export interface ChatInputHandle {
  setPrompt: (value: string) => void;
  send: () => void;
  openDropzone: () => void;
  openWhiteboard: () => void;
  openVideoUpload: () => void;
}

export const ChatInput = React.forwardRef<ChatInputHandle, ChatInputProps>(
  (
    {
      disabled,
      loading,
      placeholder,
      maxRows,
      onSubmit,
      onAbort,
      showDropZone,
      contextualAttachments,
      references,
      onRemoveReference,
      onEditAttachment,
      enableFilesUpload = true,
      enableWhiteboard = true,
      enableExamples = true,
      enableVoiceInput = true,
      enableAgentSelection = false,
      onAgentSelect,
      selectedAgent,
      sessionId,

      enableCustomToolButton = false,
      customToolButtonText = `Custom Tool`,
      onCustomToolButtonClicked,
      cmdEnterToSend = false,
      minLines = 1,
      onFocus,
      onBlur,
      onMouseEnter,
      onMouseLeave,
      showSendText = false,
      onLeaveAnchor: onLeaveAnchorProp,
    },
    ref,
  ) => {
    useSignals();

    const [prompt, setPrompt] = React.useState(``);
    const [isWaitingForSelection, setIsWaitingForSelection] = useState(false);
    const textAreaRef = React.useRef<HTMLTextAreaElement>(null);

    const ctx = useContext(EditorContext);
    const aiEditSearch = ctx?.devicePreview?.aiEditSearch;
    const aiEditWaiting = ctx?.devicePreview?.aiEditWaiting;
    const leaveAnchor = onLeaveAnchorProp ?? ctx?.devicePreview?.leaveAnchor;

    const {
      filesList,
      dropzoneProps,
      uploadedImages,
      uploadedFilesRef,
      isUploading,
      onDelete,
      reachMaxFiles,
      clearFiles,
      addFileToUploadQueue,
    } = useChatFilesUpload();
    const [isWhiteboardOpen, setIsWhiteboardOpen] = React.useState(false);
    const videoUploadRef = React.useRef<ChatVideoUploadHandle>(null);

    const isLoading = useMemo(
      () => loading || isUploading,
      [loading, isUploading],
    );

    const onSend = useCallback(() => {
      if (!isLoading && !!prompt) {
        onSubmit?.({
          role: `user`,
          content: prompt,
          created_at: chatNow(),
          images: flattenImages(uploadedImages),
          filesToUpload: uploadedFilesRef,
          contextualAttachments:
            contextualAttachments?.value?.length > 0
              ? contextualAttachments?.value
              : undefined,
          references:
            references?.value?.length > 0 ? references?.value : undefined,
        });
        setPrompt(``);
        clearFiles();
      }
    }, [
      isLoading,
      prompt,
      onSubmit,
      uploadedImages,
      uploadedFilesRef,
      clearFiles,
      contextualAttachments,
      references,
    ]);

    const onContextualAttachmentDelete = useCallback(
      (ids: string | string[]) => {
        const idsArray = Array.isArray(ids) ? ids : [ids];
        contextualAttachments.value = contextualAttachments.value.filter(
          (att) => !idsArray.includes(att.id),
        );
      },
      [],
    );

    const onWhiteboardClick = useCallback(() => {
      setIsWhiteboardOpen(true);
    }, []);

    const onStop = useCallback(() => {
      onAbort?.();
    }, [onAbort]);

    const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (cmdEnterToSend) {
        if (e.key === `Enter` && (e.metaKey || e.ctrlKey)) {
          e.preventDefault();
          onSend();
        }
      } else if (e.key === `Enter` && !e.shiftKey) {
        e.preventDefault();
        onSend();
      }
    };

    const placeholderText = useMemo(() => {
      if (disabled) return `Select an agent to start chatting...`;
      if (placeholder) return placeholder;
      return `Add a custom block using Visually AI`;
    }, [placeholder, disabled]);

    const onUploadSketch = (file: File) => {
      addFileToUploadQueue(file);
      setPrompt(
        `Build a widget using the attached image as a structural wireframe ONLY.

CRITICAL CONSTRAINTS:

No Image Usage: Do NOT use the attached image URL in the code. Use https://sdk.loomi-prod.xyz/widgets/placeholder.jpeg for all <img> tags.
Structural Reference Only: Replicate the layout, hierarchy, and element positioning (e.g., Image on top, text in middle, button at bottom).
Ignore Visuals: Disregard all colors, fonts, and specific graphics in the attachment.
Brand Styles: Apply Underoutfit brand styles instead (Poppins font, dark gray buttons, clean borders).`,
      );
    };

    useEffect(() => {
      const isVideo =
        uploadedFilesRef.some((f) => f.type.startsWith(`video/`)) ||
        filesList.some((f) => f.type?.startsWith(`video/`));

      if (isVideo && prompt?.length === 0) {
        setPrompt(`Generate a block that faithfully replicates the design of the uploaded images.
Match the layout, styles, color palette, typography, and overall visual hierarchy as closely as possible.
Ensure the content structure and tone mirror the reference,
while keeping proportions and spacing consistent for a polished and cohesive look.
The images are not to be used as assets. they are frames of a video showing different states of a ui component / interaction with a ui component.`);
      } else if (filesList?.length === 1 && prompt?.length === 0) {
        setPrompt(`Generate a block that faithfully replicates the design of the uploaded image.
Match the layout, styles, color palette, typography, and overall visual hierarchy as closely as possible.
Ensure the content structure and tone mirror the reference,
while keeping proportions and spacing consistent for a polished and cohesive look.`);
      }
    }, [filesList, uploadedFilesRef]);

    useImperativeHandle(
      ref,
      () => ({
        setPrompt,
        send: onSend,
        openDropzone: () => {
          if (!reachMaxFiles && enableFilesUpload) {
            dropzoneProps.open?.();
          }
        },
        openWhiteboard: onWhiteboardClick,
        openVideoUpload: () => videoUploadRef.current?.open(),
      }),
      [
        setPrompt,
        onSend,
        dropzoneProps.open,
        reachMaxFiles,
        enableFilesUpload,
        onWhiteboardClick,
      ],
    );

    const groupedAttachments = useComputed(() => {
      const attachments = contextualAttachments?.value || [];
      const processedIds = new Set<string>();
      const result: {
        displayAtt: ContextualAttachment;
        idsToDelete: string[];
      }[] = [];

      attachments.forEach((att) => {
        if (processedIds.has(att.id)) return;

        const isCss = att.id.endsWith(`-css`);
        const baseId = isCss ? att.id.slice(0, -4) : att.id;
        const companionId = isCss ? baseId : `${baseId}-css`;

        const companion = attachments.find((a) => a.id === companionId);
        const idsToDelete = [att.id];

        if (companion) {
          idsToDelete.push(companion.id);
          processedIds.add(companion.id);
        }
        const displayAtt = isCss && companion ? companion : att;
        processedIds.add(att.id);

        result.push({ displayAtt, idsToDelete });
      });

      return result;
    });

    const onAddNew = () => {
      setIsWaitingForSelection(true);
      aiEditSearch?.();
    };

    const onAddWithSelector = useCallback((selector: string) => {
      if (!selector.trim()) return;

      // Dispatch event to trigger section addition with the provided selector
      window.dispatchEvent(
        new CustomEvent(`aiSectionAddWithSelector`, {
          detail: { selector: selector.trim() },
        }),
      );
    }, []);

    const onCancelSelection = () => {
      setIsWaitingForSelection(false);
      const htmlSelectors =
        contextualAttachments?.value
          ?.filter((att) => att.type.includes(`html`))
          .map((att) => att.id) || [];
      aiEditWaiting?.(htmlSelectors);
    };

    useEffect(() => {
      const handleSectionAdd = () => {
        setIsWaitingForSelection(false);
      };

      window.addEventListener(`aiSectionAdd`, handleSectionAdd);
      return () => {
        window.removeEventListener(`aiSectionAdd`, handleSectionAdd);
      };
    }, []);

    useEffect(() => {
      if (textAreaRef?.current) {
        textAreaRef.current?.focus();
      }
    }, [textAreaRef]);

    return (
      <Wrapper
        disabled={disabled}
        className="ai-chat-input-wrapper"
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
      >
        {(groupedAttachments.value.length > 0 ||
          (references?.value?.length || 0) > 0) && (
          <ContextualAttachments>
            {groupedAttachments.value.map(({ displayAtt, idsToDelete }) => (
              <ChatContextualTile
                key={displayAtt.id}
                attachment={displayAtt}
                canDelete
                onDelete={() => onContextualAttachmentDelete(idsToDelete)}
                onEdit={onEditAttachment}
              />
            ))}
            {references?.value?.map((ref, idx) => (
              <ChatReferenceTile
                key={`${ref.caption}-${idx}`}
                reference={ref}
                canDelete
                onDelete={() => {
                  leaveAnchor?.();
                  onRemoveReference?.(ref);
                }}
              />
            ))}
            {!(references?.value?.length > 0) && (
              <AddContextButton
                layout
                as={motion.div}
                onClick={isWaitingForSelection ? undefined : onAddNew}
                transition={{ type: `spring`, stiffness: 400, damping: 30 }}
              >
                <InstructionText>
                  {isWaitingForSelection && <FiArrowLeft />}
                  {isWaitingForSelection ? `Hover & select component` : `+`}
                  {isWaitingForSelection && (
                    <AddContextWithSelectorButton
                      onAddWithSelector={onAddWithSelector}
                    >
                      or <u>select manually</u>
                    </AddContextWithSelectorButton>
                  )}
                  {isWaitingForSelection && (
                    <CloseButton onClick={onCancelSelection}>✕</CloseButton>
                  )}
                </InstructionText>
              </AddContextButton>
            )}
          </ContextualAttachments>
        )}
        {filesList?.length > 0 && (
          <Files>
            {filesList.map((file) => (
              <ChatImageTile
                key={file.url}
                imageUrl={file.url}
                loading={file.isUploading}
                hideImage={file.hideImage}
                canZoom
                canDelete
                onDelete={onDelete}
              />
            ))}
          </Files>
        )}
        <TransparentTextArea
          ref={textAreaRef}
          minRows={minLines ?? 1}
          maxRows={maxRows ?? 12}
          placeholder={placeholderText}
          onKeyDown={handleKeyDown}
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          onFocus={onFocus}
          onBlur={onBlur}
        />
        <Tools id="ai-chat-input-tools-wrapper">
          <div className="left">
            {enableFilesUpload && (
              <>
                <IconButton
                  onClick={dropzoneProps.open}
                  disabled={reachMaxFiles}
                >
                  <GrAttachment size={16} />
                </IconButton>
                <ChatVideoUpload
                  ref={videoUploadRef}
                  reachMaxFiles={reachMaxFiles}
                  onUpload={addFileToUploadQueue}
                />
              </>
            )}
            {enableWhiteboard && (
              <IconButton onClick={onWhiteboardClick} disabled={reachMaxFiles}>
                <BiClipboard size={17} />
              </IconButton>
            )}
            {enableAgentSelection && !sessionId && (
              <AgentSelectionDropdown
                onAgentSelect={onAgentSelect}
                selectedAgent={selectedAgent}
              />
            )}
            {enableCustomToolButton && (
              <CustomButton onClick={onCustomToolButtonClicked}>
                {customToolButtonText}
              </CustomButton>
            )}
          </div>
          <div className="right">
            {enableExamples ? <Examples>Examples</Examples> : <div />}
            {enableVoiceInput ? (
              <HiOutlineMicrophone size={18} color="#a6afb8" />
            ) : (
              <div />
            )}
            <SendButton
              as={motion.button}
              layout
              transition={{ type: `spring`, stiffness: 400, damping: 30 }}
              id="ai-chat-send-button"
              onClick={isLoading ? onStop : onSend}
              $hasContent={!!prompt}
              $showSendText={showSendText}
            >
              {showSendText && !!prompt && !isLoading && (
                <motion.span
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  style={{
                    overflow: `hidden`,
                    whiteSpace: `nowrap`,
                    fontSize: `1.4rem`,
                    fontWeight: 500,
                    marginRight: `0.8rem`,
                  }}
                >
                  Send
                </motion.span>
              )}
              {!isLoading && <FaArrowRight size={12} />}
              {isLoading && <MdStop size={15} />}
            </SendButton>
          </div>
        </Tools>
        <FileDropzone overlayVisible={showDropZone} {...dropzoneProps} />
        {isWhiteboardOpen && (
          <ToolWhiteboard
            isWhiteboardOpen={isWhiteboardOpen}
            setIsWhiteboardOpen={setIsWhiteboardOpen}
            onUploadSketch={onUploadSketch}
          />
        )}
      </Wrapper>
    );
  },
);
const Wrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  border-radius: 3rem;

  padding: 2rem 2rem;
  gap: 1rem;
  overflow: hidden;
  box-shadow: 0 3px 10px 0 rgba(0, 0, 0, 0.05),
    0 8px 10px 0 rgba(151, 210, 206, 0.22), 0 0.5px 0.5px 0 rgba(0, 0, 0, 0.15);
  border: 1px solid rgba(0, 0, 0, 0.12);
  background-color: #fff;

  &&[disabled] {
    background: #f6f5f4;

    && * {
      pointer-events: none !important;
    }
  }
`;

const Files = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  gap: 1.5rem;
`;

const ContextualAttachments = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  gap: 1rem;
  flex-wrap: wrap;
`;

const Tools = styled.div`
  width: 100%;
  display: grid;
  grid-template-columns: 1fr 1fr;
  align-items: center;
  row-gap: 2rem;
  column-gap: 0;

  && > * {
    cursor: pointer;
    user-select: none;
  }

  && .left {
    display: flex;
    flex-direction: row;
    justify-content: flex-start;
    align-items: center;
    gap: 1.5rem;
  }

  && .right {
    display: flex;
    flex-direction: row;
    justify-content: flex-end;
    align-items: center;
    gap: 1.5rem;
  }
`;

const CustomButton = styled.button`
  outline: none;
  appearance: none;
  border: 1px solid black;
  background-color: transparent;
  font-size: 1.2rem;
  font-family: Inter, sans-serif;
  font-weight: 500;
  padding: 0.5rem 1rem;
  border-radius: 2rem;
  color: black;

  cursor: pointer;
  transition: background-color 0.2s, color 0.2s;

  :hover {
    background-color: black;
    color: white;
  }
`;

const TransparentTextArea = styled(TextareaAutosize)`
  && {
    height: 100%;
    width: 100%;
    background: transparent;
    border: none;
    appearance: none;
    outline: none;
    font-family: Eesti, sans-serif;
    font-size: 1.5rem;
    resize: none;
  }
`;

const Examples = styled.div`
  color: #a6afb8;
  text-decoration: underline;
  font-family: Eesti, sans-serif;
  font-size: 1.4rem;
`;

const SendButton = styled.button<{
  $hasContent?: boolean;
  $showSendText?: boolean;
}>`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;

  background: black;
  color: white;
  border-radius: ${(props) =>
    props.$showSendText && props.$hasContent ? `2rem` : `100%`};
  height: 3.2rem;
  width: ${(props) =>
    props.$showSendText && props.$hasContent ? `auto` : `3.2rem`};
  padding: ${(props) =>
    props.$showSendText && props.$hasContent ? `0 1.2rem` : `0`};
  border: none;

  cursor: pointer;
  transition: border-radius 0.2s, width 0.2s, padding 0.2s;
`;

const IconButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  padding: 0;
  cursor: pointer;
  touch-action: manipulation;
  color: inherit;

  &:disabled {
    opacity: 0.5;
    pointer-events: none;
  }
`;

const CloseButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  border-radius: 50%;
  width: 1.8rem;
  height: 1.8rem;
  cursor: pointer;
  color: rgba(217, 70, 166, 0.46);
  font-size: 1.2rem;
  font-weight: bold;
  padding: 0;
  flex-shrink: 0;
  transition: background 0.2s;

  &:hover {
    background: rgba(217, 70, 166, 0.1);
  }
`;

const InstructionText = styled.span`
  white-space: nowrap;
  display: flex;
  align-items: center;
  gap: 0.8rem;
`;

const flattenImages = (uploadedImages: Array<string | string[]>) =>
  uploadedImages?.flatMap((item) => {
    if (Array.isArray(item)) {
      return item.map((url) => ({ url, isFrame: true } as ImageData));
    }
    return [{ url: item as string } as ImageData];
  });
